import { useAtom } from 'jotai';
import { accessibilityDataAtom, demoModeAtom, userAtom } from '@/global-store';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarInset,
  SidebarProvider,
} from '@layout/Sidebar/Sidebar';
import { Link as RouterLink, Outlet, useNavigation } from 'react-router-dom';
import { MessageCircleQuestion, MessageSquareText } from 'lucide-react';
import { useFetchAccessibilityData } from '@http/queries';
import MenuList from './Sidebar/MenuList/MenuList';
import Header from '@layout/Header/Header';
import { Button, useTailwindBreakpoint } from '@eye-able/ui';
import Footer from '@layout/Footer';
import LoadingSpinner from '@components/LoadingSpinner';
import { usePathname } from '@hooks/usePathname';
import { cn } from '@helpers/cn';
import { isInternalTeamUser } from '@helpers/isInternalTeamUser';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';
import { FloatingDemoOfferActions } from './FloatingDemoOfferActions';
import { useSidebar } from '@layout/Sidebar/SidebarContext';

export function MainLayout() {
  const xlAndUp = useTailwindBreakpoint('xl');

  return (
    <SidebarProvider
      className='eye:flex-col'
      style={{
        ['--app-header-height']: `${xlAndUp ? 75 : 52}px`,
      }}
    >
      <MainLayoutContent />
    </SidebarProvider>
  );
}

// Separate component so it can read the sidebar context that MainLayout provides.
function MainLayoutContent() {
  const [, setAccessibilityData] = useAtom(accessibilityDataAtom);
  const [demoMode] = useAtom(demoModeAtom);
  const [user] = useAtom(userAtom);
  const { isMobile, openMobile } = useSidebar();

  const shouldShowDemoActions = demoMode && isMobile && !openMobile;

  const { t } = useInlineTranslation();

  const path = usePathname();

  const isReportArea = path.startsWith('/report');
  const isRulesArea = path.startsWith('/rulesOverview');

  // fetch the accessibility data for the current auditEntry and update the atom if the data changes
  const { isLoading } = useFetchAccessibilityData({
    onDataChanged: (data) => setAccessibilityData(data),
  });

  const navigation = useNavigation();

  const isPageLoading =
    navigation.state === 'loading' ||
    ((isReportArea || isRulesArea) && isLoading);

  const renderMainContent = () => {
    if (isPageLoading) {
      return <LoadingSpinner />;
    }
    return <Outlet />;
  };

  const shouldShowForInternalFeedback = isInternalTeamUser(user);

  return (
    <>
      <Header />
      <div aria-hidden style={{ height: 'var(--app-header-height)' }} />
      <div
        className='eye:bg-grey-10 eye:flex eye:w-full eye:flex-1'
        style={{ paddingRight: 'var(--removed-body-scroll-bar-size, 0px)' }}
      >
        <Sidebar
          className='no-print'
          collapsible='icon'
          side='left'
          variant='inset'
        >
          <SidebarContent className='eye:bg-white'>
            <MenuList />
          </SidebarContent>
          {!demoMode && (
            <SidebarFooter className='eye:gap-xs'>
              {shouldShowForInternalFeedback && (
                <Button className='eye:w-full' variant='outline' asChild>
                  <a
                    href='https://form.asana.com/?k=UFeLGkl2Ortj6cEohzwSrA&d=1199244943732308'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <MessageSquareText aria-hidden />
                    {t('productFeedback')}
                  </a>
                </Button>
              )}
              <div id='support-popup'>
                <Button className='eye:w-full' variant='outline' asChild>
                  <RouterLink to='/help-center'>
                    <MessageCircleQuestion aria-hidden />
                    {t('helpCenter')}
                  </RouterLink>
                </Button>
              </div>
            </SidebarFooter>
          )}
        </Sidebar>

        <div
          className={cn(
            'eye:min-w-0 eye:max-w-full',
            'eye:px-16 eye:pt-32 eye:sm:px-6 eye:sm:py-6 eye:lg:px-48 eye:lg:py-[40px]',
            'eye:flex eye:flex-col eye:flex-1 eye:flex-nowrap',
            'eye:overflow-x-clip eye:wrap-break-word',
            shouldShowDemoActions ? 'eye:pb-[100px]!' : 'eye:pb-32',
          )}
        >
          <SidebarInset className='eye:w-full eye:max-w-[1500px] eye:mx-auto'>
            {renderMainContent()}
            <Footer />
          </SidebarInset>
        </div>
      </div>

      {shouldShowDemoActions ? <FloatingDemoOfferActions /> : null}
    </>
  );
}
